import {
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Grid,
  Icon,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import FilterListIcon from "@mui/icons-material/FilterList";
import App from "./App";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ClearIcon from "@mui/icons-material/Clear";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useContext, useEffect, useState, useRef } from "react";

import { AuthContext } from "../index";
import { GlobalDataContext } from "../index";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import _ from "lodash";
import { WheelPicker, WheelPickerWrapper } from "@ncdai/react-wheel-picker";
import "@ncdai/react-wheel-picker/style.css";

import {
  LocalizationProvider,
  StaticDatePicker,
  TimeField,
} from "@mui/x-date-pickers";

import "./MainScreen.css";
import ItemsList from "./ItemsList";
import ItemsListV2 from "./ItemsListV2";
import SwipableImageCarousel from "./SwipableImageCarousel";
import { default as AddEmptyServiceInfoV2 } from "./InfoV2";
import { default as FilterInfoV2 } from "./InfoV2";
import {
  getDay,
  getWeekday,
  getDateString,
  getHourMinutes,
  startOfDay,
  endOfDay,
} from "../utils/dateTime";
import { getImageV3 } from "../utils/images";

import BIM_LOGO from "../images/bim_logo_100.png";

const HOUR_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: i + 10,
  label: String(i + 10).padStart(2, "0"),
}));
const MINUTE_OPTIONS = [0, 15, 30, 45].map((minute) => ({
  value: minute,
  label: String(minute).padStart(2, "0"),
}));
const TIME_WHEEL_CLASS_NAMES = {
  optionItem: "time-wheel-option",
  highlightItem: "time-wheel-highlight",
};

function AppScreen() {
  const calendarButtonRef = useRef(null);
  const { id, userId, phoneNumber, onLogin, onLogout } =
    useContext(AuthContext);
  const { gsFilters, setGSFilters, filters, setFilters } =
    useContext(GlobalDataContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const fillById = (id, arr = []) => {
    const ix = arr.findIndex((it) => it.id == id);
    if (ix != -1) {
      return arr[ix];
    }

    return { id };
  };

  const today = () => DateTime.now();

  const _getValueFromFilter = (type, arr = []) =>
    arr.find((it) => it?.type === type)?.value;

  const plusDaysToDate = (days) => {
    setFilters((prev) =>
      prev.map((it) =>
        it.type.startsWith("timeFrame")
          ? {
              ...it,
              value: DateTime.fromSeconds(it.value).plus({ days }).toSeconds(),
            }
          : it,
      ),
    );
  };

  const DEFAULT_FILTER_INFO_STATE = {
    open: false,
    filters: [...filters],
    calendar: {
      open: false,
    },
  };
  const [filterInfo, setFilterInfo] = useState(DEFAULT_FILTER_INFO_STATE);

  const [showCalendar, setShowCalendar] = useState(false);

  const _handleFilterModalOpen = () => {
    setFilterInfo((prevState) => ({
      ...prevState,
      open: true,
    }));
  };

  const _handleFilterModalClose = () => {
    setFilterInfo((prevState) => ({
      ...prevState,
      open: false,
    }));
  };

  const handleDatePickV2 = (newDate, type) => {
    setFilterInfo((prevState) => {
      const nextFilters = [...(prevState?.filters ?? [])];

      if (type == "from") {
        const ixFrom = nextFilters.findIndex(
          (fltr) => fltr.type == "timeFrameFrom",
        );
        if (ixFrom != -1) {
          nextFilters.splice(ixFrom, 1);
        }

        nextFilters.push({
          type: "timeFrameFrom",
          value: startOfDay(newDate / 1000),
        });
      }

      if (type == "to") {
        const ixTo = nextFilters.findIndex(
          (fltr) => fltr.type == "timeFrameTo",
        );
        if (ixTo != -1) {
          nextFilters.splice(ixTo, 1);
        }

        nextFilters.push({
          type: "timeFrameTo",
          value: endOfDay(newDate / 1000),
        });
      }

      return {
        ...prevState,
        calendar: { ...prevState?.calendar, openFrom: false, openTo: false },
        filters: [...nextFilters],
      };
    });
  };

  const [specialistServices, setSpecialistServices] = useState({
    isLoaded: false,
    data: [],
  });

  useEffect(() => {
    setSpecialistServices((prevState) => ({ ...prevState, isLoaded: false }));
    setFilterInfo(DEFAULT_FILTER_INFO_STATE);
  }, [filters]);

  async function loadServices() {
    try {
      const resp = await axios.post(`/api/v1/services/get`, {
        specialistIds: [id],
        timeFrame: {
          from: _getValueFromFilter("timeFrameFrom", filters),
          to: _getValueFromFilter("timeFrameTo", filters),
        },
      });

      setSpecialistServices({ isLoaded: true, data: resp?.data });
    } catch (err) {
      throw err;
    }
  }

  useEffect(() => {
    if (userId && !specialistServices.isLoaded) {
      loadServices();
    }
  }, [userId, specialistServices.isLoaded]);

  const DEFAULT_EMPTY_SERVICE = {
    open: false,
    calendar: {
      open: false,
      date: DateTime.now(),
    },
    time: {
      open: false,
      from: DateTime.now().set({ hour: "10", minute: "00" }),
      to: DateTime.now().set({ hour: "20", minute: "00" }),
    },
  };
  const [emptyService, setEmptyService] = useState(DEFAULT_EMPTY_SERVICE);

  const handleAddEmptyService = () => {
    setEmptyService((ps) => ({ ...ps, open: true }));
  };

  const handleDeleteService = async (serviceId) => {
    try {
      const resp = await axios.delete(`/api/v1/services/${serviceId}`);
      loadServices();
    } catch (err) {
      throw err;
    }
  };

  const handleCancelService = async (serviceId) => {
    try {
      const resp = await axios.put(`/api/v1/services`, {
        id: serviceId,
        specialist: {
          id: null,
        },
      });
      setServiceDetailed(null);
      setSpecialistServices((prevState) => ({ ...prevState, isLoaded: false }));
    } catch (err) {
      throw err;
    }
  };

  const handleCreateEmptyService = () => {
    // do checks

    async function createEmptyService() {
      try {
        const resp = await axios.post(`/api/v1/services`, {
          specialist: {
            id: id,
          },
          timeFrame: {
            from: emptyService?.time?.from?.toUnixInteger(),
            to: emptyService?.time?.to?.toUnixInteger(),
          },
        });

        setEmptyService(DEFAULT_EMPTY_SERVICE);
        loadServices();
      } catch (err) {
        throw err;
      }
    }

    createEmptyService();
  };

  const handleDefaultEmptyService = () => {
    setEmptyService((ps) => ({ ...DEFAULT_EMPTY_SERVICE, open: true }));
  };

  const [serviceDetailed, setServiceDetailed] = useState(null);
  return (
    <App
      withNavigation
      top={
        <Stack
          sx={{
            backgroundColor: "#fcfcfc",
            boxShadow:
              "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
            isolation: "isolate",
          }}
        >
          <Stack
            direction="row"
            sx={{
              p: 2,
              pb: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{ height: "14px", mr: "4px" }}
              component={"img"}
              alt={"Логотип компании Bim"}
              src={BIM_LOGO}
            />

            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "300",
                color: "gray",
                alignSelf: "flex-end",
                lineHeight: 1,
              }}
            >
              | специалисты
            </Typography>
          </Stack>
          <Stack
            spacing={2}
            direction="row"
            sx={{
              p: 2,
              pt: 1,
              flex: 1,
              flexGrow: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Badge
                badgeContent={
                  filters.filter((it) => it.type != "timeFrameTo").length
                }
                color={"primary"}
                onClick={_handleFilterModalOpen}
                sx={{
                  "&:hover": { cursor: "pointer" },
                  borderRadius: "15px",
                  p: "2px",
                  border: "1px solid gray",
                }}
              >
                <FilterListIcon sx={{ color: "gray" }} />
              </Badge>
            </Box>
            <Box
              sx={{
                display: { xs: "flex" },
                borderRadius: "30px",
                border: "1px solid #ddd",
                justifyContent: "center",
                alignItems: "center",
                p: "5px",
                height: "40px",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
                width: "60%",
              }}
              onClick={() => {
                // setFilterInfo(prevState => ({...prevState, open: true}));
              }}
            >
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                  width: "100%",
                }}
                display={"column"}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    width: "100%",
                  }}
                  onClick={_handleFilterModalOpen}
                >
                  {getDay(today()) !=
                    getDay(_getValueFromFilter("timeFrameFrom", filters)) &&
                  getDay(_getValueFromFilter("timeFrameFrom", filters)) ==
                    getDay(_getValueFromFilter("timeFrameTo", filters)) ? (
                    <Box
                      sx={{
                        width: "24px",
                        borderRadius: "50%",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        plusDaysToDate(-1);
                      }}
                    >
                      {getDay(today()) !=
                        getDay(_getValueFromFilter("timeFrameFrom", filters)) &&
                      getDay(_getValueFromFilter("timeFrameFrom", filters)) ==
                        getDay(_getValueFromFilter("timeFrameTo", filters)) ? (
                        <ArrowBackIosIcon
                          fontSize={"small"}
                          sx={{
                            color: "gray",
                            ml: 1,
                            display: "block",
                          }}
                        />
                      ) : null}
                    </Box>
                  ) : null}
                  <Typography
                    fontSize={"18px"}
                    sx={{ color: "gray" }}
                    align={"center"}
                  >
                    <CalendarMonthIcon
                      sx={{ fontSize: "12px", fontWeight: "bold", pr: "2px" }}
                    />
                    {getDay(_getValueFromFilter("timeFrameFrom", filters))}
                    {getDay(_getValueFromFilter("timeFrameFrom", filters)) !=
                    getDay(
                      _getValueFromFilter("timeFrameTo", filters) + 100000,
                    ) ? (
                      <>&nbsp;&ndash;&nbsp;</>
                    ) : null}
                    {getDay(_getValueFromFilter("timeFrameFrom", filters)) !=
                    getDay(_getValueFromFilter("timeFrameTo", filters) + 100000)
                      ? getDay(_getValueFromFilter("timeFrameTo", filters))
                      : null}
                  </Typography>
                  {getDay(_getValueFromFilter("timeFrameFrom", filters)) ==
                  getDay(_getValueFromFilter("timeFrameTo", filters)) ? (
                    <Box
                      sx={{
                        width: "24px",
                        borderRadius: "50%",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        plusDaysToDate(1);
                      }}
                    >
                      {getDay(_getValueFromFilter("timeFrameFrom", filters)) ==
                      getDay(_getValueFromFilter("timeFrameTo", filters)) ? (
                        <ArrowForwardIosIcon
                          fontSize={"small"}
                          sx={{
                            color: "gray",
                            mr: 1,
                            display: "block",
                          }}
                        />
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
              </Stack>
            </Box>
            <AddIcon
              fontSize={"large"}
              sx={{
                m: 0,
                color: "gray",
                fontWeight: "300",
                // border: '1px solid gray',
                // borderRadius: '50%'
              }}
              onClick={handleAddEmptyService}
            />
          </Stack>
        </Stack>
      }
    >
      {/*  helper components */}
      <Modal
        open={emptyService.open}
        sx={{
          zIndex: 1305,
        }}
      >
        <Box sx={{ height: "100dvh", overflowY: "auto" }}>
          <Icon
            onClick={() => {
              setEmptyService((ps) => ({ ...ps, open: false }));
            }}
            sx={{
              zIndex: 1306,
              p: "5px",
              backgroundColor: "#eee",
              borderRadius: "50%",
              position: "absolute",
              top: "10px",
              right: "10px",
            }}
          >
            <ClearIcon />
          </Icon>
          <Stack
            sx={{
              height: "100%",
              justifyContent: "space-between",
              backgroundColor: "#fff",
            }}
          >
            <Box sx={{ p: 2, pt: 4, pb: 4 }}>
              <Typography
                align={"center"}
                sx={{ fontWeight: "bold", fontSize: "22px" }}
              >
                Рабочее время
              </Typography>
            </Box>
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                sx={{
                  pt: 1,
                  pb: 1,
                  pr: 1,
                }}
              >
                <CalendarMonthIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
                <Stack
                  direction={"row"}
                  sx={{
                    width: "100%",
                    pb: 1,
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "rgb(0 0 0 / 0.01)",
                    },
                  }}
                  onClick={() => {
                    setEmptyService((ps) => ({
                      ...ps,
                      calendar: { ...ps?.calendar, open: !ps?.calendar?.open },
                    }));
                  }}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Stack>
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      Дата
                    </Typography>
                    <Typography>
                      {getDay(emptyService?.calendar?.date)}
                    </Typography>
                  </Stack>
                </Stack>
                <ArrowForwardIosIcon
                  onClick={() => {
                    setEmptyService((ps) => ({
                      ...ps,
                      calendar: { ...ps?.calendar, open: !ps?.calendar?.open },
                    }));
                  }}
                  sx={{
                    p: 2,
                    color: "rgb(0 0 0/0.75)",
                    fontSize: "16px",
                    rotate: emptyService.calendar.open ? "90deg" : "none",
                  }}
                />
              </Stack>
              {emptyService?.calendar?.open ? (
                <LocalizationProvider
                  dateAdapter={AdapterLuxon}
                  adapterLocale={"ru"}
                >
                  <StaticDatePicker
                    onChange={(newDate) => {
                      const from = newDate.set({ hour: "10", minute: "00" });
                      const to = newDate.set({ hour: "20", minute: "00" });
                      setEmptyService((ps) => ({
                        ...ps,
                        calendar: {
                          ...ps?.calendar,
                          open: false,
                          date: newDate,
                        },
                        time: { from: from, to: to },
                      }));
                    }}
                    onClose={() => {
                      setEmptyService((ps) => ({
                        ...ps,
                        calendar: { ...ps?.calendar, open: false },
                      }));
                    }}
                    views={["month", "day"]}
                    value={emptyService?.calendar?.date}
                    disablePast
                    slotProps={{
                      toolbar: {
                        hidden: true,
                      },
                    }}
                    slots={{
                      actionBar: () => null,
                    }}
                  />
                </LocalizationProvider>
              ) : null}
              <Stack
                direction={"row"}
                alignItems={"center"}
                sx={{
                  pt: 1,
                  pb: 1,
                  pr: 1,
                }}
              >
                <Stack
                  sx={{
                    width: "100%",
                    pb: 1,
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "rgb(0 0 0 / 0.01)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "max-content minmax(0, 1fr) minmax(0, 1fr) max-content",
                      columnGap: 1,
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box />
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      С
                    </Typography>
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      По
                    </Typography>
                    <Box />

                    <AccessTimeIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
                    <LocalizationProvider
                      dateAdapter={AdapterLuxon}
                      adapterLocale={"ru"}
                    >
                      <TimeField
                        value={emptyService?.time?.from}
                        disabled={true}
                        size={"small"}
                        sx={{ width: "100%" }}
                      />
                    </LocalizationProvider>
                    <LocalizationProvider
                      dateAdapter={AdapterLuxon}
                      adapterLocale={"ru"}
                    >
                      <TimeField
                        value={emptyService?.time?.to}
                        disabled={true}
                        size={"small"}
                        sx={{ width: "100%" }}
                      />
                    </LocalizationProvider>

                    <ArrowForwardIosIcon
                      onClick={() => {
                        setEmptyService((ps) => ({
                          ...ps,
                          time: { ...ps?.time, open: !ps?.time?.open },
                        }));
                      }}
                      sx={{
                        justifySelf: "end",
                        p: 2,
                        color: "rgb(0 0 0/0.75)",
                        fontSize: "16px",
                        rotate: emptyService.time.open ? "90deg" : "none",
                      }}
                    />

                    {emptyService?.time?.open ? <Box /> : null}
                    {emptyService?.time?.open ? (
                      <WheelPickerWrapper className={"time-wheel-wrapper time-wheel-wrapper-centered"}>
                        <WheelPicker
                          options={HOUR_OPTIONS}
                          value={emptyService?.time?.from?.hour ?? 10}
                          onValueChange={(value) => {
                            setEmptyService((ps) => ({
                              ...ps,
                              time: {
                                ...ps.time,
                                from: (ps?.time?.from ?? DateTime.now()).set({
                                  hour: value,
                                }),
                              },
                            }));
                          }}
                          infinite
                          visibleCount={12}
                          classNames={TIME_WHEEL_CLASS_NAMES}
                        />
                        <WheelPicker
                          options={MINUTE_OPTIONS}
                          value={emptyService?.time?.from?.minute ?? 0}
                          onValueChange={(value) => {
                            setEmptyService((ps) => ({
                              ...ps,
                              time: {
                                ...ps.time,
                                from: (ps?.time?.from ?? DateTime.now()).set({
                                  minute: value,
                                }),
                              },
                            }));
                          }}
                          infinite
                          visibleCount={12}
                          classNames={TIME_WHEEL_CLASS_NAMES}
                        />
                      </WheelPickerWrapper>
                    ) : null}
                    {emptyService?.time?.open ? (
                      <WheelPickerWrapper className={"time-wheel-wrapper time-wheel-wrapper-centered"}>
                        <WheelPicker
                          options={HOUR_OPTIONS}
                          value={emptyService?.time?.to?.hour ?? 20}
                          onValueChange={(value) => {
                            setEmptyService((ps) => ({
                              ...ps,
                              time: {
                                ...ps.time,
                                to: (ps?.time?.to ?? DateTime.now()).set({
                                  hour: value,
                                }),
                              },
                            }));
                          }}
                          infinite
                          visibleCount={12}
                          classNames={TIME_WHEEL_CLASS_NAMES}
                        />
                        <WheelPicker
                          options={MINUTE_OPTIONS}
                          value={emptyService?.time?.to?.minute ?? 0}
                          onValueChange={(value) => {
                            setEmptyService((ps) => ({
                              ...ps,
                              time: {
                                ...ps.time,
                                to: (ps?.time?.to ?? DateTime.now()).set({
                                  minute: value,
                                }),
                              },
                            }));
                          }}
                          infinite
                          visibleCount={12}
                          classNames={TIME_WHEEL_CLASS_NAMES}
                        />
                      </WheelPickerWrapper>
                    ) : null}
                  </Box>
                </Stack>
              </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
              <Stack
                direction={"row"}
                sx={{ justifyContent: "center" }}
                spacing={2}
              >
                {/*<Button sx={{mt:4, width: '100%'}} variant={'outlined'}*/}
                {/*        onClick={ handleDefaultEmptyService }*/}
                {/*        sx={{*/}
                {/*            flex: '1 1 auto',*/}
                {/*            maxWidth: '300px'*/}
                {/*        }}*/}
                {/*>*/}
                {/*    Сбросить*/}
                {/*</Button>*/}
                <Button
                  sx={{ mt: 4, width: "100%" }}
                  variant={"contained"}
                  onClick={handleCreateEmptyService}
                  sx={{
                    flex: "1 1 auto",
                    maxWidth: "400px",
                  }}
                >
                  Добавить
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Modal>
      <AddEmptyServiceInfoV2
        // open={emptyService.open}
        open={false}
        handleClose={() => {
          setEmptyService((ps) => ({ ...ps, open: false }));
        }}
      >
        <Stack
          sx={{
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ p: 2, pt: 4, pb: 4 }}>
            <Typography
              align={"center"}
              sx={{ fontWeight: "bold", fontSize: "22px" }}
            >
              Новый слот
            </Typography>
          </Box>
          <Box sx={{ overflowY: "auto", height: "100%" }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              sx={{
                pt: 1,
                pb: 1,
                pr: 1,
              }}
            >
              <CalendarMonthIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
              <Stack
                direction={"row"}
                sx={{
                  width: "100%",
                  pb: 1,
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "rgb(0 0 0 / 0.01)",
                  },
                }}
                onClick={() => {
                  setEmptyService((ps) => ({
                    ...ps,
                    calendar: { ...ps?.calendar, open: !ps?.calendar?.open },
                  }));
                }}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack>
                  <Typography
                    fontSize={"12px"}
                    sx={{ color: "rgb(0 0 0/ 0.7)" }}
                  >
                    Дата
                  </Typography>
                  <Typography>
                    {getDay(emptyService?.calendar?.date)}
                  </Typography>
                </Stack>
              </Stack>
              <ArrowForwardIosIcon
                onClick={() => {
                  setEmptyService((ps) => ({
                    ...ps,
                    calendar: { ...ps?.calendar, open: !ps?.calendar?.open },
                  }));
                }}
                sx={{
                  p: 2,
                  color: "rgb(0 0 0/0.75)",
                  fontSize: "16px",
                  rotate: emptyService.calendar.open ? "90deg" : "none",
                }}
              />
            </Stack>
            {emptyService?.calendar?.open ? (
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={"ru"}
              >
                <StaticDatePicker
                  onChange={(newDate) => {
                    const from = newDate.set({ hour: "10", minute: "00" });
                    const to = newDate.set({ hour: "20", minute: "00" });
                    setEmptyService((ps) => ({
                      ...ps,
                      calendar: { ...ps?.calendar, open: false, date: newDate },
                      time: { from: from, to: to },
                    }));
                  }}
                  onClose={() => {
                    setEmptyService((ps) => ({
                      ...ps,
                      calendar: { ...ps?.calendar, open: false },
                    }));
                  }}
                  views={["month", "day"]}
                  value={emptyService?.calendar?.date}
                  disablePast
                  slotProps={{
                    toolbar: {
                      hidden: true,
                    },
                  }}
                  slots={{
                    actionBar: () => null,
                  }}
                />
              </LocalizationProvider>
            ) : null}
            <Stack
              direction={"row"}
              alignItems={"center"}
              sx={{
                pt: 1,
                pb: 1,
                pr: 1,
              }}
            >
              <AccessTimeIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
              <Stack
                spacing={1}
                direction={"row"}
                sx={{
                  width: "100%",
                  pb: 1,
                  pr: 6,
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: "rgb(0 0 0 / 0.01)",
                  },
                }}
                // onClick={() => { setShowCalendar(prevState => !prevState)}}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack>
                  <Typography
                    fontSize={"12px"}
                    sx={{ color: "rgb(0 0 0/ 0.7)" }}
                  >
                    С
                  </Typography>
                  <LocalizationProvider
                    dateAdapter={AdapterLuxon}
                    adapterLocale={"ru"}
                  >
                    <TimeField
                      value={emptyService?.time?.from}
                      onChange={(newTime) => {
                        setEmptyService((ps) => ({
                          ...ps,
                          time: { ...ps.time, from: newTime },
                        }));
                      }}
                      size={"small"}
                    />
                  </LocalizationProvider>
                  {/*{ getValueFromFilterReadable('date')}*/}
                </Stack>
                <Stack>
                  <Typography
                    fontSize={"12px"}
                    sx={{ color: "rgb(0 0 0/ 0.7)" }}
                  >
                    По
                  </Typography>
                  <LocalizationProvider
                    dateAdapter={AdapterLuxon}
                    adapterLocale={"ru"}
                  >
                    <TimeField
                      value={emptyService?.time?.to}
                      onChange={(newTime) => {
                        setEmptyService((ps) => ({
                          ...ps,
                          time: { ...ps.time, to: newTime },
                        }));
                      }}
                      size={"small"}
                    />
                  </LocalizationProvider>
                </Stack>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack
              direction={"row"}
              sx={{ justifyContent: "center" }}
              spacing={2}
            >
              <Button
                sx={{ mt: 4, width: "100%" }}
                variant={"outlined"}
                onClick={handleDefaultEmptyService}
                sx={{
                  flex: "1 1 auto",
                  maxWidth: "300px",
                }}
              >
                Сбросить
              </Button>
              <Button
                sx={{ mt: 4, width: "100%" }}
                variant={"contained"}
                onClick={handleCreateEmptyService}
                sx={{
                  flex: "1 1 auto",
                  maxWidth: "300px",
                }}
              >
                Добавить
              </Button>
            </Stack>
          </Box>
        </Stack>
      </AddEmptyServiceInfoV2>
      <Grid spacing={1} justifyContent="flex-start" alignItems="flex-start">
        <Grid
          item
          sx={{
            height: "100%",
            m: { xs: 1 },
          }}
        >
          <Box>
            {specialistServices.data
              .sort(
                (left, right) => left?.timeFrame?.from - right?.timeFrame?.from,
              )
              .reduce((acc, curr) => {
                //todo: change
                const dayString = getDateString(
                  DateTime.fromSeconds(curr.timeFrame.from),
                );
                const dayStringFormatted =
                  getDay(curr.timeFrame.from) +
                  " - " +
                  getWeekday(curr.timeFrame.from);
                const idx = acc.findIndex((it) => it.dayString == dayString);

                if (idx != -1) {
                  acc[idx].data.push(curr);
                } else {
                  acc.push({
                    dayStringFormatted: dayStringFormatted,
                    dayString: dayString,
                    data: [curr],
                  });
                }

                return acc;
              }, [])
              .map((sg) => (
                <>
                  <Typography align={"left"} color={"gray"} fontSize={"20px"}>
                    {sg.dayStringFormatted}
                  </Typography>
                  <ItemsListV2
                    itemsLoading={!specialistServices.isLoaded}
                    closeDetailed={() => {
                      setServiceDetailed(null);
                    }}
                    items={
                      sg.data.length ? (
                        sg.data.map((item) => (
                          <Box
                            sx={{
                              borderRadius: "24px",
                              boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
                            }}
                            onClick={() => {
                              setServiceDetailed(item);
                            }}
                          >
                            <CardActionArea
                              disableRipple
                              sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "flex-start",
                              }}
                              onClick={() => {
                                setServiceDetailed(item);
                              }}
                            >
                              {item?.serviceTemplate?.id ? (
                                <CardMedia
                                  component="img"
                                  sx={{
                                    width: 100,
                                    height: 100,
                                    m: "2px",
                                    borderRadius: "24px",
                                  }}
                                  image={getImageV3(
                                    _.nth(
                                      item?.serviceTemplate?.images.map(
                                        (im) => im?.id,
                                      ),
                                      0,
                                    ),
                                  )}
                                  alt=""
                                />
                              ) : null}
                              <CardContent
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "flex-start",
                                  height: "100%",
                                  width: "100%",
                                }}
                              >
                                {item?.serviceTemplate?.id ? (
                                  <Typography
                                    sx={{ fontSize: "16px" }}
                                    align={"left"}
                                  >
                                    {" "}
                                    {` ${item?.serviceTemplate?.title}: ${getHourMinutes(item?.timeFrame?.from)} - ${getHourMinutes(item?.timeFrame?.to)}`}
                                  </Typography>
                                ) : (
                                  <Typography
                                    sx={{ fontSize: "16px" }}
                                    align={"left"}
                                  >
                                    {" "}
                                    {`Рабочее время: ${getHourMinutes(item?.timeFrame?.from)} - ${getHourMinutes(item?.timeFrame?.to)}`}{" "}
                                  </Typography>
                                )}
                                {item?.serviceTemplate?.id ? (
                                  item?.client?.id ? (
                                    <Typography
                                      align={"left"}
                                      fontSize={"14px"}
                                    >
                                      {" "}
                                      Занято{" "}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      align={"left"}
                                      fontSize={"14px"}
                                    >
                                      {" "}
                                      Без клиента{" "}
                                    </Typography>
                                  )
                                ) : null}
                              </CardContent>
                            </CardActionArea>
                          </Box>
                        ))
                      ) : (
                        <Typography sx={{ color: "gray", fontSize: "14px" }}>
                          Сервисов для указанных фильтров не найдено
                        </Typography>
                      )
                    }
                    itemDetailed={
                      serviceDetailed ? (
                        <Card
                          elevation={1}
                          sx={{
                            display: "flex",
                            height: "100dvh",
                            backgroundColor: "#fff",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              overflowY: "auto",
                              height: "100%",
                            }}
                          >
                            {serviceDetailed?.serviceTemplate?.images
                              ?.length ? (
                              <SwipableImageCarousel
                                images={
                                  serviceDetailed?.serviceTemplate?.images || []
                                }
                                aspectRatio={"2/1"}
                                getImage={() => {}}
                                imageUrls={serviceDetailed?.serviceTemplate?.images?.map(
                                  (it) => getImageV3(it?.id),
                                )}
                                navigation={"fraction"}
                              />
                            ) : null}
                            <CardContent
                              sx={{
                                position: "relative",
                                marginTop: "-25px",
                                backgroundColor: "#fff",
                                borderTopLeftRadius: "25px",
                                borderTopRightRadius: "25px",
                              }}
                            >
                              <Typography
                                align="center"
                                sx={{ pb: 2, fontSize: "24px" }}
                              >
                                {serviceDetailed?.serviceTemplate?.title}
                              </Typography>
                              <Typography
                                align="center"
                                sx={{ fontSize: "12px", color: "gray" }}
                              >
                                {serviceDetailed?.serviceTemplate?.description}
                              </Typography>
                              {serviceDetailed?.client?.id ? (
                                <>
                                  <Typography sx={{ pt: 2 }} align={"left"}>
                                    Клиент
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "12px" }}
                                    align="left"
                                  >
                                    {serviceDetailed?.client?.firstName}
                                  </Typography>
                                </>
                              ) : null}

                              {serviceDetailed?.customizations?.wish?.data ? (
                                <>
                                  <Typography sx={{ pt: 2 }} align={"left"}>
                                    Пожелания
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "12px" }}
                                    align="left"
                                  >
                                    {
                                      serviceDetailed?.customizations?.wish
                                        ?.data
                                    }
                                  </Typography>
                                </>
                              ) : null}
                              {serviceDetailed?.customizations?.cosmetics
                                ?.data ? (
                                <>
                                  <Typography sx={{ pt: 2 }} align={"left"}>
                                    Косметика
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "12px" }}
                                    align="left"
                                  >
                                    {
                                      serviceDetailed?.customizations?.cosmetics?.data?.filter(
                                        (it) => it.checked,
                                      )[0].title
                                    }
                                  </Typography>
                                </>
                              ) : null}
                              <Typography sx={{ pt: 2, mb: 2 }} align={"left"}>
                                Место и время
                              </Typography>
                              <Stack sx={{ justifyContent: "flex-end" }}>
                                <Typography
                                  sx={{ pb: 1 }}
                                >{`${getDay(serviceDetailed?.timeFrame?.from) + " с " + getHourMinutes(serviceDetailed?.timeFrame?.from) + " по " + getHourMinutes(serviceDetailed?.timeFrame?.to)}`}</Typography>
                              </Stack>
                              <Stack sx={{ justifyContent: "flex-end" }}>
                                <Typography sx={{ pb: 1 }}>
                                  {serviceDetailed?.location?.address}
                                </Typography>
                              </Stack>
                            </CardContent>
                          </Box>
                          <Box sx={{ p: 2 }}>
                            <Stack
                              direction={"row"}
                              sx={{ justifyContent: "center" }}
                              spacing={2}
                            >
                              <Button
                                disabled={serviceDetailed?.client?.id}
                                variant={"contained"}
                                onClick={() => {
                                  handleCancelService(serviceDetailed?.id);
                                }}
                                sx={{
                                  flex: "1 1 auto",
                                  maxWidth: "400px",
                                }}
                              >
                                Отменить слот
                              </Button>
                            </Stack>
                            {serviceDetailed?.client?.id ? (
                              <Typography fontSize={"10px"} align={"center"}>
                                * Для отмены услуги с клиентом обратитесь к
                                администратору
                              </Typography>
                            ) : null}
                          </Box>
                        </Card>
                      ) : null
                    }
                  />
                </>
              ))}
          </Box>
        </Grid>
      </Grid>
      {/*    helper components*/}
      <FilterInfoV2
        open={filterInfo.open}
        handleClose={_handleFilterModalClose}
      >
        <Stack
          sx={{
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ p: 2, pt: 4, pb: 4 }}>
            <Typography
              align={"center"}
              sx={{ fontWeight: "bold", fontSize: "22px" }}
            >
              Фильтры
            </Typography>
          </Box>
          <Box sx={{ overflowY: "auto", height: "100%" }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              sx={{
                pt: 1,
                pb: 1,
                pr: 1,
              }}
            >
              <CalendarMonthIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
              <Stack
                direction={"row"}
                sx={{
                  width: "100%",
                  // borderBottom: '1px solid rgb(0 0 0 / 0.1)',
                  pb: 1,
                }}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack
                  sx={{
                    flex: "1 1 auto",
                    justifyContent: "space-between",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "rgb(0 0 0 / 0.01)",
                    },
                  }}
                  direction={"row"}
                  onClick={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: {
                        ...prevState.calendar,
                        openFrom: !prevState?.calendar?.openFrom,
                        openTo: false,
                      },
                    }));
                    setShowCalendar((prevState) => !prevState);
                  }}
                >
                  <Stack>
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      С даты
                    </Typography>
                    <Typography>
                      {getDay(
                        _getValueFromFilter(
                          "timeFrameFrom",
                          filterInfo?.filters,
                        ),
                      )}
                    </Typography>
                  </Stack>
                  <ArrowForwardIosIcon
                    sx={{
                      p: 2,
                      color: "rgb(0 0 0/0.75)",
                      fontSize: "16px",
                      rotate: filterInfo.calendar.openFrom ? "90deg" : "none",
                    }}
                    // onClick={() => {
                    //     setFilterInfo(prevState => ({...prevState, calendar: {...prevState.calendar, openFrom: !prevState?.calendar?.openFrom, openTO: false}}))
                    //     setShowCalendar(prevState => !prevState)
                    // }}
                  />
                </Stack>
                <Stack
                  sx={{
                    flex: "1 1 auto",
                    justifyContent: "space-between",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "rgb(0 0 0 / 0.01)",
                    },
                  }}
                  direction={"row"}
                  onClick={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: {
                        ...prevState.calendar,
                        openTo: !prevState?.calendar?.openTo,
                        openFrom: false,
                      },
                    }));
                    setShowCalendar((prevState) => !prevState);
                  }}
                >
                  <Stack>
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      По дату
                    </Typography>
                    <Typography>
                      {getDay(
                        _getValueFromFilter("timeFrameTo", filterInfo?.filters),
                      )}
                    </Typography>
                  </Stack>
                  <ArrowForwardIosIcon
                    sx={{
                      p: 2,
                      color: "rgb(0 0 0/0.75)",
                      fontSize: "16px",
                      rotate: filterInfo.calendar.openTo ? "90deg" : "none",
                    }}
                    // onClick={() => {
                    //     setFilterInfo(prevState => ({...prevState, calendar: {...prevState.calendar, open: !prevState?.calendar?.open}}))
                    //     setShowCalendar(prevState => !prevState)
                    // }}
                  />
                </Stack>
              </Stack>
              {/*<ArrowForwardIosIcon*/}
              {/*    sx={{*/}
              {/*        p: 2,*/}
              {/*        color: 'rgb(0 0 0/0.75)',*/}
              {/*        fontSize: '16px',*/}
              {/*        rotate: filterInfo.calendar.open ? '90deg': 'none'*/}
              {/*    }}*/}
              {/*                     onClick={() => {*/}
              {/*                         setFilterInfo(prevState => ({...prevState, calendar: {...prevState.calendar, open: !prevState?.calendar?.open}}))*/}
              {/*                         setShowCalendar(prevState => !prevState)*/}
              {/*                     }}*/}
              {/*/>*/}
            </Stack>
            {filterInfo?.calendar?.openFrom ? (
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={"ru"}
              >
                <StaticDatePicker
                  onChange={(newDate) => {
                    handleDatePickV2(newDate, "from");
                  }}
                  onClose={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: { ...prevState?.calendar, openFrom: false },
                    }));
                  }}
                  views={["month", "day"]}
                  value={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameFrom", filterInfo?.filters),
                  )}
                  disablePast
                  maxDate={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameTo", filterInfo?.filters),
                  )}
                  slotProps={{
                    toolbar: {
                      hidden: true,
                    },
                  }}
                  slots={{
                    actionBar: () => null,
                  }}
                />
              </LocalizationProvider>
            ) : null}
            {filterInfo?.calendar?.openTo ? (
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={"ru"}
              >
                <StaticDatePicker
                  onChange={(newDate) => {
                    handleDatePickV2(newDate, "to");
                  }}
                  onClose={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: { ...prevState?.calendar, openTo: false },
                    }));
                  }}
                  views={["month", "day"]}
                  value={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameTo", filterInfo?.filters),
                  )}
                  disablePast
                  minDate={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameFrom", filterInfo?.filters),
                  )}
                  slotProps={{
                    toolbar: {
                      hidden: true,
                    },
                  }}
                  slots={{
                    actionBar: () => null,
                  }}
                />
              </LocalizationProvider>
            ) : null}
          </Box>

          <Box sx={{ p: 2 }}>
            <Stack
              direction={"row"}
              sx={{ justifyContent: "center" }}
              spacing={2}
            >
              <Button
                variant={"outlined"}
                onClick={() => {
                  setFilterInfo({ ...DEFAULT_FILTER_INFO_STATE, open: true });
                }}
                sx={{
                  flex: "1 1 auto",
                  maxWidth: "300px",
                }}
              >
                Сбросить
              </Button>
              <Button
                variant={"contained"}
                onClick={() => {
                  setFilters([...filterInfo.filters]);
                  setSpecialistServices((prevState) => ({
                    ...prevState,
                    isLoaded: false,
                  }));
                  setFilterInfo((prevState) => ({ ...prevState, open: false }));
                }}
                sx={{
                  flex: "1 1 auto",
                  maxWidth: "300px",
                }}
              >
                Применить
              </Button>
            </Stack>
          </Box>
        </Stack>
      </FilterInfoV2>
    </App>
  );
}

export default AppScreen;
